import { useState, useEffect } from "react";
import {
  ArrowLeft,
  Phone,
  Mail,
  Calendar,
  Building2,
  FileText,
  User,
  Shield,
  Edit,
  Trash2,
  Cake,
  Gift,
  StickyNote,
  Check,
  X,
} from "lucide-react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/vibe-code/rcm-v2/components/ui/card";
import { Badge } from "src/vibe-code/rcm-v2/components/ui/badge";
import { Separator } from "src/vibe-code/rcm-v2/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/vibe-code/rcm-v2/components/ui/tabs";
import { Textarea } from "src/vibe-code/rcm-v2/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/vibe-code/rcm-v2/components/ui/alert-dialog";
import { EmployeeForm } from "./forms/EmployeeForm";
import type { Employee, ActivityLog, Dealer } from "src/vibe-code/rcm-v2/types";
import { useEmployeeDetails } from "src/vibe-code/rcm-api";
import { formatDistanceToNow, format } from "date-fns";
import { isValidDate } from "src/utils";
import { formatEmployeeRole, formatEmployeeStatus } from "../types";

interface EmployeeProfileProps {
  employee: Employee;
  dealers: Dealer[];
  onBack: () => void;
  onDealerClick: (dealerId: string) => void;
  onUpdate?: (updatedEmployee: Employee) => void;
  onDelete?: (employeeId: string) => void;
}

export function EmployeeProfile({
  employee,
  dealers,
  onBack,
  onDealerClick,
  onUpdate,
  onDelete,
}: EmployeeProfileProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loadingActivityLogs, setLoadingActivityLogs] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { getEmployeeActivityLogs, createEmployeeNote } = useEmployeeDetails();

  // Fetch activity logs when employee changes
  useEffect(() => {
    const fetchData = async () => {
      if (employee.id) {
        setLoadingActivityLogs(true);

        try {
          const logs = await getEmployeeActivityLogs(employee.id);
          setActivityLogs(logs);
        } catch (error) {
          console.error("Error fetching employee activity logs:", error);
        } finally {
          setLoadingActivityLogs(false);
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee.id]); // Only depend on employee.id, not the functions to avoid infinite re-renders

  const handleSave = (updatedEmployee: Partial<Employee>) => {
    // In a real app, this would update the employee via API
    console.log("Updating employee:", updatedEmployee);

    const fullUpdatedEmployee: Employee = {
      ...employee,
      ...updatedEmployee,
      updated_at: new Date().toISOString(),
    };

    if (onUpdate) {
      onUpdate(fullUpdatedEmployee);
    }
    setIsEditing(false);
  };

  const handleAddNote = () => {
    setIsAddingNote(true);
  };

  const handleSaveNote = async () => {
    if (newNote.trim()) {
      const success = await createEmployeeNote(employee.id, {
        note: newNote.trim(),
      });
      if (success) {
        // Refresh activity logs to show the new note
        const logs = await getEmployeeActivityLogs(employee.id);
        setActivityLogs(logs);
        setNewNote("");
        setIsAddingNote(false);
      }
    }
  };

  const handleCancelNote = () => {
    setNewNote("");
    setIsAddingNote(false);
  };

  const handleEmailEmployee = () => {
    const emailAddresses = [employee.email];
    if (employee.additional_emails && employee.additional_emails.length > 0) {
      emailAddresses.push(...employee.additional_emails);
    }
    const mailtoLink = `mailto:${emailAddresses.join(",")}`;
    window.open(mailtoLink, "_self");
  };

  const handleDelete = () => {
    // In a real app, this would delete employee via API
    console.log("Delete employee:", employee.id);
    onDelete?.(employee.id);
    setIsDeleteDialogOpen(false);
  };
  if (isEditing) {
    return (
      <EmployeeForm
        employee={employee}
        dealers={dealers}
        onSave={handleSave}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={onBack}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Employees</span>
          </Button>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold">
                {employee.first_name} {employee.last_name}
              </h1>
              {employee.role === "GENERAL_MANAGER" && (
                <div title="GM Permissions">
                  <Shield className="w-6 h-6 text-accent" />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Employee
          </Button>
          <AlertDialog
            open={isDeleteDialogOpen}
            onOpenChange={setIsDeleteDialogOpen}
          >
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                className="text-destructive border-destructive hover:bg-destructive hover:text-destructive-foreground"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Are you sure you want to delete this employee?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete the
                  employee "{employee.first_name} {employee.last_name}" and
                  remove their data from the system.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Yes, Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="activity">
            Activity ({activityLogs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Employee Information */}
            <Card className="shadow-custom-md">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <User className="w-5 h-5" />
                  <span>Employee Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Employee Name
                  </label>
                  <p className="mt-1 font-medium">
                    {employee.first_name} {employee.last_name}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Role (Dealer Portal Permissions)
                  </label>
                  <Badge variant="outline" className="mt-1">
                    {formatEmployeeRole(employee.role)}
                  </Badge>
                </div>

                {employee.title && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Title
                    </label>
                    <p className="mt-1 font-medium">{employee.title}</p>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Employee Status
                  </label>
                  <Badge variant="outline" className="mt-1">
                    {formatEmployeeStatus(employee.status)}
                  </Badge>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Direct Line
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{employee.phone_number}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Email
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="break-all">{employee.email}</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Birthday
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Cake className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {isValidDate(employee.birthday)
                        ? new Intl.DateTimeFormat("en-US", {
                            timeZone: "UTC",
                          }).format(new Date(employee.birthday!))
                        : ""}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Additional Email Addresses
                  </label>
                  <div className="mt-1 space-y-1">
                    {employee.additional_emails &&
                    employee.additional_emails.length > 0 ? (
                      employee.additional_emails.map(
                        (email: string, index: number) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm break-all">{email}</span>
                          </div>
                        ),
                      )
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground italic">
                          No additional emails
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    onClick={handleEmailEmployee}
                    variant="outline"
                    size="sm"
                    className="w-full flex items-center justify-center space-x-2"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Email Employee</span>
                  </Button>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Activity Date
                  </label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {employee.last_activity ? (
                        new Date(employee.last_activity).toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground italic">
                          No activity recorded
                        </span>
                      )}
                    </span>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-1 gap-4 text-sm">
                  <div>
                    <label className="text-muted-foreground">Unique ID</label>
                    <p className="font-medium font-mono">{employee.id}</p>
                  </div>
                  <div>
                    <label className="text-muted-foreground">
                      Associated Dealer ID
                    </label>
                    <p className="font-medium font-mono">
                      {employee.dealer.id}
                    </p>
                  </div>
                  <div>
                    <label className="text-muted-foreground">Created</label>
                    <div className="text-sm text-gray-500">
                      {employee.created_at &&
                      !isNaN(new Date(employee.created_at).getTime())
                        ? format(new Date(employee.created_at), "MMM d, yyyy")
                        : "No date available"}
                    </div>
                  </div>
                  <div>
                    <label className="text-muted-foreground">Updated</label>
                    <p className="font-medium">
                      {employee.updated_at &&
                      !isNaN(new Date(employee.updated_at).getTime())
                        ? formatDistanceToNow(new Date(employee.updated_at), {
                            addSuffix: true,
                          })
                        : "Never updated"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Associated Dealer */}
            <Card className="shadow-custom-md">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="w-5 h-5" />
                  <span>Associated Dealer</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <button
                  onClick={() => onDealerClick(employee.dealer.id)}
                  className="w-full p-4 text-left border rounded-lg hover:shadow-custom-md transition-shadow bg-muted/30 hover:bg-muted/50"
                >
                  <h4 className="font-medium text-primary hover:text-primary-dark mb-2">
                    {employee.dealer.name}
                  </h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Building2 className="w-4 h-4" />
                      <span>Store Number: {employee.dealer.store_number}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <span>View dealer profile →</span>
                    </div>
                  </div>
                </button>
              </CardContent>
            </Card>

            {/* Quick Stats & Employee Profile Notes */}
            <Card className="shadow-custom-md">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <StickyNote className="w-5 h-5" />
                  <span>Employee Profile Notes</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {employee.profile_notes ? (
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {employee.profile_notes}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    No profile notes added
                  </p>
                )}

                <Separator />

                <div>
                  <CardTitle className="text-base mb-3">Quick Stats</CardTitle>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Gift Sent</span>
                      <Badge
                        variant={employee.gift_sent ? "default" : "secondary"}
                      >
                        {employee.gift_sent ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="shadow-custom-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>Activity Log ({activityLogs.length})</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleAddNote}>
                  Add Note
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isAddingNote && (
                <div className="p-4 border rounded-lg bg-muted/30 mb-4">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground mb-2 block">
                        New Note
                      </label>
                      <Textarea
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        placeholder="Add a note about this employee..."
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelNote}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveNote}>
                        <Check className="w-4 h-4 mr-1" />
                        Save Note
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {loadingActivityLogs ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">
                      Loading activity logs...
                    </p>
                  </div>
                ) : activityLogs.length > 0 ? (
                  <div className="space-y-3">
                    {activityLogs.map((log, index) => (
                      <Card key={index} className="shadow-custom-sm">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  Note
                                </Badge>
                                <span className="text-sm text-muted-foreground">
                                  by {log.created_by}
                                </span>
                              </div>
                              <p className="text-sm mb-2">{log.description}</p>
                              <p className="text-xs text-muted-foreground">
                                {log.created_at &&
                                !isNaN(new Date(log.created_at).getTime())
                                  ? format(
                                      new Date(log.created_at),
                                      "MMM d, yyyy 'at' h:mm a",
                                    )
                                  : "No date available"}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-medium mb-2">No activity yet</h3>
                    <p className="text-muted-foreground mb-4">
                      No activity logs have been recorded for this employee.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
